/**
 * The seam between the chat runtime and whoever can actually execute a page
 * tool.
 *
 * Eve can hand a tool call back to the browser instead of resolving it
 * server-side. The chat runtime owns the lifecycle — the pending state, the
 * 20s deadline, the exactly-once result post (`chatActivity.ts`, the client
 * tool machine) — and knows nothing about which tools exist or how to run one.
 * The WebMCP lane registers those halves here from its own module, without
 * touching the chat's rendering:
 *
 *   registerPageToolsProvider(() => [...what this page offers Emjae])
 *   registerClientToolExecutor(async (call, signal) => { ...run the page tool })
 *
 * The third half, the transport, is registered by the runtime itself. A result
 * does not merely go back: it RESUMES a parked turn on `/chat-hub/stream`, and
 * the answer streams in on that same request, so the only module that can post
 * one is the module that owns the conversation and the event handler.
 *
 * With no executor registered, every call expires at its deadline — the turn
 * continues with an honest error instead of hanging, which is the contract's
 * floor. With no provider registered, no page tools are offered at all, which
 * is what every browser without WebMCP looks like. With no poster registered —
 * only outside a mounted runtime — results are dropped with a console.warn,
 * because a loud drop beats a fake success.
 */
import type { ClientToolCall, ClientToolResult, PageToolDescriptor } from './types';

export type ClientToolExecutor = (
  call: Pick<ClientToolCall, 'id' | 'name' | 'args'>,
  /** Aborted when the deadline passes — a slow executor must stop, not land late. */
  signal: AbortSignal,
) => Promise<unknown>;

export type ClientToolResultPoster = (result: ClientToolResult) => Promise<void>;

/**
 * What the page can run, asked for fresh as each turn is sent, because the
 * answer changes with the page. Synchronous on purpose: it is read while the
 * request body is being built, and a promise there would either delay every
 * turn or race the send.
 */
export type PageToolsProvider = () => readonly PageToolDescriptor[];

let executor: ClientToolExecutor | null = null;
let poster: ClientToolResultPoster | null = null;
let pageTools: PageToolsProvider | null = null;

export function registerClientToolExecutor(next: ClientToolExecutor | null): void {
  executor = next;
}

export function registerClientToolResultPoster(next: ClientToolResultPoster | null): void {
  poster = next;
}

export function registerPageToolsProvider(next: PageToolsProvider | null): void {
  pageTools = next;
}

export function getClientToolExecutor(): ClientToolExecutor | null {
  return executor;
}

/**
 * The page tools to send with the turn being built now.
 *
 * Empty with no provider registered, which is every browser without the agent
 * surface. That is what keeps this seam invisible to them: an empty list means
 * the backend renders no page-tools block, so Emjae is never told about a
 * capability that is not there.
 */
export function currentPageTools(): readonly PageToolDescriptor[] {
  return pageTools?.() ?? [];
}

export async function postClientToolResult(result: ClientToolResult): Promise<void> {
  if (!poster) {
    console.warn(
      '[central-chat] no client-tool result transport registered; dropping result for',
      result.tool_use_id,
      result,
    );
    return;
  }
  await poster(result);
}
